import { useTranslation } from 'react-i18next';
import {
  ApiErrorText,
  Loader,
  ModalComponentWrapper,
  ItemsListEmptyState,
} from '@/shared/components';
import { cn } from '@/shared/utils';
import { useBanksDictionary } from '@/shared/api';
import {
  InvoiceHistoryContent,
  type InvoiceHistoryItem,
  type InvoiceHistoryResponse,
  useInvoiceDetailsDialog,
  useInvoiceHistory,
  useInvoiceHistoryDialog,
} from '@/features/deposit-bank-transfer';

interface InvoiceHistoryModalProps {
  onBack?: () => void;
  onClose?: () => void;
  invoices?: InvoiceHistoryResponse;
  className?: string;
}

export function InvoiceHistoryModal({
  onBack,
  invoices,
}: InvoiceHistoryModalProps) {
  const { t } = useTranslation();
  const { openInvoiceDetailsDialog } = useInvoiceDetailsDialog();
  const { openInvoiceHistoryDialog } = useInvoiceHistoryDialog();
  const {
    data: invoiceHistory,
    isLoading: isHistoryLoading,
    isError: isHistoryError,
    error: historyError,
  } = useInvoiceHistory();
  const { banksData, isBanksLoading, banksError, isBanksError } =
    useBanksDictionary();

  const handleInvoiceClick = (invoice: InvoiceHistoryItem) => {
    openInvoiceDetailsDialog({
      mode: 'view',
      invoiceData: invoice,
      onBack: () => openInvoiceHistoryDialog({ onBack, onClose: onBack }),
      onClose: () => openInvoiceHistoryDialog({ onBack, onClose: onBack }),
    });
  };

  const isLoading = isHistoryLoading || isBanksLoading;
  const isError = isHistoryError || isBanksError;
  const firstError = historyError || banksError;
  const isEmpty = !invoiceHistory?.length && !invoices?.length;

  const getBankName = (bankKey: string) => {
    if (!banksData?.value?.deposit?.countries) {
      return t('depositMethods.bankTransfer.invoiceHistory.bankName');
    }

    for (const country of banksData.value.deposit.countries) {
      if (country.banks) {
        const bank = country.banks.find(
          (b: { bankKey: string; bankName: string }) => b.bankKey === bankKey
        );
        if (bank) {
          return bank.bankName;
        }
      }
    }

    return t('depositMethods.bankTransfer.invoiceHistory.bankName');
  };

  const rawInvoices = invoices || invoiceHistory || [];

  const invoicesList = rawInvoices.map(invoice => ({
    ...invoice,
    bankName: getBankName(invoice.bankKey),
  }));

  return (
    <ModalComponentWrapper
      title="depositMethods.bankTransfer.invoiceHistory.title"
      onBack={onBack}>
      <div
        className={cn(
          'flex flex-col items-center justify-center gap-[3.2rem] min-h-0 h-full',
          isEmpty ? 'h-[38rem]' : ''
        )}>
        {isLoading ? (
          <Loader />
        ) : isError ? (
          <ApiErrorText error={firstError} />
        ) : isEmpty ? (
          <ItemsListEmptyState
            title={t('depositMethods.bankTransfer.invoiceHistory.emptyTitle')}
            description={t(
              'depositMethods.bankTransfer.invoiceHistory.emptyDescription'
            )}
          />
        ) : (
          <InvoiceHistoryContent
            invoices={invoicesList}
            onInvoiceClick={handleInvoiceClick}
          />
        )}
      </div>
    </ModalComponentWrapper>
  );
}
